import { useState } from "react";
import type { Lang } from "../lib/i18n";
import { pick } from "../lib/i18n";
import { submitRating } from "../lib/state";

/** Star-rating form (KP). Embedded inside the rating gate on the completion screen. */
export function RatingForm({ lang, email, onClose, embedded = false }: { lang: Lang; email: string; onClose: () => void; embedded?: boolean }) {
  const t = (ar: string, en: string) => pick(lang, ar, en);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [done, setDone] = useState(false);

  function submit() {
    if (rating === 0) return;
    submitRating(email || "anonymous", rating, comment);
    setDone(true);
    setTimeout(onClose, 1800);
  }

  return (
    <div className={`kd-rating-popup${embedded ? " kd-rating-embedded" : ""}`} dir={lang === "ar" ? "rtl" : "ltr"} role="dialog">
      {!embedded && (
        <button className="kd-rating-close" onClick={onClose} aria-label={t("إغلاق", "Close")}>×</button>
      )}
      {done ? (
        <div className="kd-rating-thanks">
          <div className="kd-rating-thanks-icon">✓</div>
          <h3>{t("شكراً جزيلاً!", "Thank you!")}</h3>
          <p>{t("تم تسجيل تقييمك بنجاح.", "Your feedback has been recorded.")}</p>
        </div>
      ) : (
        <>
          <h3 className="kd-rating-title">{t("قيّم تجربتك", "Rate your experience")}</h3>
          <p className="kd-rating-subtitle">
            {t("كيف كانت رحلة التأهيل الرقمي؟ تقييمك يساعدنا على تحسينها.", "How was your digital onboarding journey? Your feedback helps us improve.")}
          </p>
          <div className="kd-rating-stars" onMouseLeave={() => setHover(0)}>
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                className={`kd-rating-star${(hover || rating) >= star ? " is-on" : ""}`}
                onClick={() => setRating(star)}
                onMouseEnter={() => setHover(star)}
                aria-label={`${star} ${t("نجوم", "stars")}`}
              >
                ★
              </button>
            ))}
          </div>
          {rating > 0 && (
            <textarea
              className="kd-rating-comment"
              rows={3}
              placeholder={t("اكتب ملاحظاتك (اختياري)", "Write your feedback (optional)")}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          )}
          <button className="kd-btn-primary kd-btn-gold" onClick={submit} disabled={rating === 0}>
            {t("إرسال التقييم", "Submit rating")}
          </button>
        </>
      )}
    </div>
  );
}

/** Full-screen rating gate (qP) shown over the certificate until the user rates. */
export function RatingGate({ lang, email, name, onClose }: { lang: Lang; email: string; name: string; onClose: () => void }) {
  const t = (ar: string, en: string) => pick(lang, ar, en);
  return (
    <div className="kd-rating-gate" dir={lang === "ar" ? "rtl" : "ltr"} role="dialog" aria-modal="true">
      <div className="kd-rating-gate-card">
        <h1 className="kd-rating-gate-title">{t("تهانينا، لقد أتممت رحلة التأهيل", "Congratulations, you completed the onboarding journey")}</h1>
        <p className="kd-rating-gate-subtitle">
          {t(
            `${name ? name + "، " : ""}يُرجى تقييم تجربتك للحصول على شهادتك ومراجعة الخطوات التالية.`,
            `${name ? name + ", " : ""}please rate your experience to view your certificate and the next steps.`,
          )}
        </p>
        <RatingForm lang={lang} email={email} onClose={onClose} embedded />
      </div>
    </div>
  );
}
